import type { MyBetPart } from '@ps/isp-web-services-types';
import { MarketLevel } from '@ps/isp-web-services-types';
import type { Values } from '@ps/web-core-intl';
import classNames from 'clsx';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { SportsLink } from '@swf/sports/components';
import { OpportunityDescription } from '@swf/sports/components/OpportunityDescription/OpportunityDescription';
import { EventTypeId } from '@swf/sports/types/eventType';
import { getRaceTimeFromRaceId } from '@swf/sports/utils/getRaceTimeFromRaceId';

import { useSportsMyBetsWidget } from '../../../../../../../../model';
import {
  isActiveAntePostBasedMyBetPart,
  isActiveEventBasedMyBetPart,
  isActiveMeetingBasedMyBetPart
} from '../../../../../../../../utils/isActivePart';

import styles from './EventLink.scss';

export interface Props {
  isSettled?: boolean;
  part: MyBetPart;
  isSgm?: boolean;
}

const EventLink: React.FC<Props> = ({ part, isSettled, isSgm }) => {
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const {
    widgetConfig: { isEmbedded }
  } = useSportsMyBetsWidget();

  const { eventDescription, inPlay, marketLevels, startTime, venue, ...restPart } = part;

  const isActivePart =
    !isSettled &&
    [
      isActiveEventBasedMyBetPart,
      isActiveMeetingBasedMyBetPart,
      isActiveAntePostBasedMyBetPart
    ].some((testFn) => testFn(part));

  if (!isSelfExcluded && !isEmbedded && ((isSettled && inPlay) || isActivePart)) {
    const getLinkRoute = () => {
      const isGolfMatchBet =
        'eventTypeId' in restPart &&
        restPart.eventTypeId === EventTypeId.GOLF &&
        marketLevels?.includes(MarketLevel.SUB_COMPETITION);

      if (isGolfMatchBet) {
        return 'sportsGolfMatches';
      }

      const isOutrightBet = marketLevels?.includes(MarketLevel.COMPETITION);

      if (isOutrightBet) {
        return 'sportsCompetitionOutrights';
      }

      const isAntepostBet = isActiveAntePostBasedMyBetPart(part);

      if (isAntepostBet) {
        return 'sportsAnimalRacingFutureMarket';
      }

      return isActiveEventBasedMyBetPart(part) ? 'sportsEvent' : 'sportsAnimalRacingRace';
    };

    const linkAlias = getLinkRoute();

    const raceId = isActiveMeetingBasedMyBetPart(part)
      ? getRaceTimeFromRaceId(part.raceId)
      : undefined;

    return (
      <div className={classNames(styles.link, { [styles.sgm]: isSgm })}>
        <SportsLink alias={linkAlias} values={{ ...(restPart as Values), raceId }}>
          <OpportunityDescription
            variant="link"
            description={eventDescription}
            venue={venue}
            marketTime={startTime}
          />
        </SportsLink>
      </div>
    );
  }

  return (
    <span className={classNames({ [styles.sgm]: isSgm })}>
      <OpportunityDescription
        variant="link"
        description={eventDescription}
        venue={venue}
        marketTime={startTime}
      />
    </span>
  );
};

export default EventLink;
